<script setup lang="ts">
// Panel 5: who is making the visits. A creator's name narrows the shared spec
// through the observation ledger's own creator filter, so the rest of the
// question follows; the same click on the active observer clears it. A null id
// has no column the ledger can select, so it is plain text rather than a dead
// button.
import { computed } from "vue";

import { useStoreMapReview } from "@/store/map-review";
import type { MapReviewWasteCreator, MapReviewWasteView } from "@/type/api";

import ReportPanel from "../report/ReportPanel.vue";
import { formatCount, wasteShare } from "./wasteTheme";

const props = defineProps<{
	view: MapReviewWasteView;
}>();

const store = useStoreMapReview();

/** The creator clause in force, read from the same observation/creator enum
 * filter `filterByObserver` writes, so the highlight is the spec's own state
 * rather than a second copy of it. */
const activeObserver = computed<number | null>(() => {
	for (const clause of store.spec.filters) {
		if (
			clause.table !== "observation" ||
			clause.column !== "creator" ||
			clause.op !== "enum"
		) {
			continue;
		}
		const values = clause.values ?? [];
		if (values.length !== 1) continue;
		const id = Number(values[0]);
		if (Number.isFinite(id)) return id;
	}
	return null;
});

/** The applied-filter chip's tooltip: every clause the shared spec carries,
 * including the ones the waste panels ignore. */
const filterTitle = computed(() =>
	store.appliedFilters
		.map((clause) =>
			clause.detail ? `${clause.label} — ${clause.detail}` : clause.label,
		)
		.join("\n"),
);

function selectObserver(creator: MapReviewWasteCreator): void {
	if (creator.id === null) return;
	store.filterByObserver(creator.id);
}
</script>

<template>
	<section class="waste-observers">
		<ReportPanel title="Who is making the visits">
			<!-- The chip is the panel's own reassurance that a narrowed table is
			     narrowed by the shared spec, not by a silent default. Its tooltip
			     names every clause, ignored ones included. -->
			<div v-if="store.specNarrowed" class="filter-row">
				<span
					class="filter-chip"
					:title="filterTitle"
					:aria-label="`${store.appliedFilters.length} applied filter clauses`"
				>
					<i class="bi bi-funnel"></i>
					{{ store.appliedFilters.length }}
					{{ store.appliedFilters.length === 1 ? "clause" : "clauses" }}
				</span>
				<span class="filter-hint">applied to the shared selection</span>
			</div>

			<p class="panel-help">
				Visits attributed to the observer who recorded most of them that day,
				with the share that ended in a treatment. Read it as a question about
				workload and site mix, not as a ranking of people: a technician whose
				round is clean sites will show a low share and is not thereby wasting
				effort.
			</p>

			<p v-if="view.creators.length === 0" class="panel-empty">
				No visit in the current selection carries an observer.
			</p>
			<table v-else class="observer-table">
				<thead>
					<tr>
						<th>observer</th>
						<th class="num">visits</th>
						<th class="num">treated share</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="creator in view.creators"
						:key="creator.id ?? 'unattributed'"
						:class="{
							active: creator.id !== null && creator.id === activeObserver,
						}"
					>
						<td>
							<button
								v-if="creator.id !== null"
								type="button"
								class="link"
								:aria-pressed="creator.id === activeObserver"
								:title="
									creator.id === activeObserver
										? 'Click again to clear this observer filter'
										: `Narrow the selection to ${creator.name}`
								"
								@click="selectObserver(creator)"
							>
								{{ creator.name }}
							</button>
							<span v-else>Unattributed</span>
						</td>
						<td class="num">{{ formatCount(creator.visits) }}</td>
						<td class="num">
							{{ wasteShare(creator.treated, creator.visits).toFixed(0) }}%
						</td>
					</tr>
				</tbody>
			</table>

			<template #help>
				<p>
					Each visit is attributed to the person who recorded most of the
					visit's observations; a tie or an unrecorded creator is
					"Unattributed". Visits are visit-day counts, exactly as the panels
					above; the treated share is the part of those visits that applied a
					treatment.
				</p>
				<p>
					The treated share is a description of the round, not a performance
					measure. A low share can mean a technician spent the day checking
					clean sites, and a high share can mean a round that met a lot of
					production. Click a name to narrow the whole question to that
					observer; click it again to clear.
				</p>
			</template>
		</ReportPanel>
	</section>
</template>

<style scoped>
.waste-observers {
	grid-column: 1 / -1;
	min-width: 0;
}

.filter-row {
	display: flex;
	align-items: center;
	gap: 6px;
	margin: 0 0 6px;
	min-width: 0;
}

/* The chip's count is secondary to the table, so it stays small and its detail
   lives in the tooltip: one hover names every clause the spec carries. */
.filter-chip {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	flex: 0 0 auto;
	padding: 1px 7px;
	border: 1px solid #a5d8ff;
	border-radius: 10px;
	background: #e7f5ff;
	color: #1864ab;
	font-size: 0.64rem;
	line-height: 1.6;
	font-variant-numeric: tabular-nums;
	cursor: help;
}

.filter-chip .bi {
	font-size: 0.62rem;
}

.filter-hint {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 0.64rem;
	color: #6c757d;
}

.panel-help {
	margin: 0 0 8px;
	font-size: 0.68rem;
	line-height: 1.35;
	color: #6c757d;
}

.panel-empty {
	margin: 0;
	font-size: 0.72rem;
	color: #6c757d;
}

.observer-table {
	width: 100%;
	table-layout: fixed;
	border-collapse: collapse;
	font-size: 0.7rem;
}

/* The name takes 62% of the table now that the treated column is gone; the two
   counts share the rest. A name may wrap but does not truncate. */
.observer-table thead th:nth-child(1) {
	width: 62%;
}

.observer-table thead th:nth-child(2) {
	width: 18%;
}

.observer-table thead th:nth-child(3) {
	width: 20%;
}

.observer-table th,
.observer-table td {
	padding: 4px 6px;
	border-bottom: 1px solid #eef0f2;
	text-align: left;
	vertical-align: middle;
}

.observer-table th {
	color: #6c757d;
	font-size: 0.64rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

.observer-table tbody tr:last-child td {
	border-bottom: 0;
}

.observer-table .num {
	text-align: right;
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
}

.observer-table tr.active td {
	background: #e7f1ff;
}

.link {
	max-width: 100%;
	padding: 0;
	border: 0;
	background: none;
	color: #0d6efd;
	font: inherit;
	text-align: left;
	white-space: normal;
	overflow-wrap: anywhere;
	cursor: pointer;
}

.link:hover {
	text-decoration: underline;
}

.link:focus-visible {
	outline: 2px solid #0d6efd;
	outline-offset: 2px;
}

.observer-table tr.active .link {
	color: #1864ab;
	font-weight: 600;
}
</style>
